/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CompleteFlightInstanceProps {
  id: string;
  completeFlightInstanceName: string;
  url: string;
  username: string;
  apiKey: string;
  isPrimary: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CompleteFlightInstanceRecord extends Record<CompleteFlightInstanceProps>({
  id: null as any,
  completeFlightInstanceName: null as any,
  url: null as any,
  username: null as any,
  apiKey: null as any,
  isPrimary: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    completeFlightInstanceName: string;
    url: string;
    username: string;
    apiKey: string;
    isPrimary: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CompleteFlightInstanceRecord {
    if (json == null) {
      throw new Error('CompleteFlightInstanceRecord.fromJS: parameter cannot be null');
    }
    return new CompleteFlightInstanceRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CompleteFlightInstanceJson.id',
      ),
      completeFlightInstanceName: getFromOrThrow(
        json,
        (j) => j.completeFlightInstanceName,
        'CompleteFlightInstanceJson.completeFlightInstanceName',
      ),
      url: getFromOrThrow(json, (j) => j.url, 'CompleteFlightInstanceJson.url'),
      username: getFromOrThrow(json, (j) => j.username, 'CompleteFlightInstanceJson.username'),
      apiKey: getFromOrThrow(json, (j) => j.apiKey, 'CompleteFlightInstanceJson.apiKey'),
      isPrimary: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isPrimary', false),
        'CompleteFlightInstanceJson.isPrimary',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    completeFlightInstanceName: string;
    url: string;
    username: string;
    apiKey: string;
    isPrimary: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CompleteFlightInstanceRecord, Error[]] {
    if (json == null) {
      throw new Error('CompleteFlightInstanceRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CompleteFlightInstanceRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CompleteFlightInstanceJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      completeFlightInstanceName: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.completeFlightInstanceName,
            'CompleteFlightInstanceJson.completeFlightInstanceName',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      url: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.url, 'CompleteFlightInstanceJson.url'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      username: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.username, 'CompleteFlightInstanceJson.username'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      apiKey: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.apiKey, 'CompleteFlightInstanceJson.apiKey'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPrimary: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isPrimary', false),
            'CompleteFlightInstanceJson.isPrimary',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CompleteFlightInstanceProps) {
    super(props);
    if (props == null) {
      throw new Error('CompleteFlightInstanceRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CompleteFlightInstanceViewModelBase implements CompleteFlightInstanceProps {
  protected readonly _props: CompleteFlightInstanceProps;
  private _json?: CompleteFlightInstanceProps;

  get id(): string {
    return this._props.id;
  }
  get completeFlightInstanceName(): string {
    return this._props.completeFlightInstanceName;
  }
  get url(): string {
    return this._props.url;
  }
  get username(): string {
    return this._props.username;
  }
  get apiKey(): string {
    return this._props.apiKey;
  }
  get isPrimary(): boolean {
    return this._props.isPrimary;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CompleteFlightInstanceProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        completeFlightInstanceName: this._props.completeFlightInstanceName,
        url: this._props.url,
        username: this._props.username,
        apiKey: this._props.apiKey,
        isPrimary: this._props.isPrimary,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCompleteFlightInstanceEntities = (state: AppState) =>
  state.entityStores.completeFlightInstance.entities;

export const selectCompleteFlightInstanceById = (id: string) => (state: AppState) =>
  state.entityStores.completeFlightInstance.entities.get(id);

export const selectCompleteFlightInstanceMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.completeFlightInstance.meta.get(id);

export const selectCompleteFlightInstanceAndMetadataById = (id: string) =>
  createSelector(
    selectCompleteFlightInstanceById(id),
    selectCompleteFlightInstanceMetadataById(id),
    (completeFlightInstance, meta) => ({ completeFlightInstance, meta }),
  );

export function selectCompleteFlightInstanceEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCompleteFlightInstanceEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CompleteFlightInstanceRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCompleteFlightInstanceEntitiesByCompleteFlightInstanceName(
  completeFlightInstanceName: string,
) {
  return createSelector(selectAllCompleteFlightInstanceEntities, (entities) =>
    entities.filter((e) => e.completeFlightInstanceName === completeFlightInstanceName).valueSeq(),
  );
}

export function selectCompleteFlightInstanceEntitiesByUrl(url: string) {
  return createSelector(selectAllCompleteFlightInstanceEntities, (entities) =>
    entities.filter((e) => e.url === url).valueSeq(),
  );
}

export function selectCompleteFlightInstanceEntitiesByUsername(username: string) {
  return createSelector(selectAllCompleteFlightInstanceEntities, (entities) =>
    entities.filter((e) => e.username === username).valueSeq(),
  );
}

export function selectCompleteFlightInstanceEntitiesByApiKey(apiKey: string) {
  return createSelector(selectAllCompleteFlightInstanceEntities, (entities) =>
    entities.filter((e) => e.apiKey === apiKey).valueSeq(),
  );
}

export function selectCompleteFlightInstanceEntitiesByIsPrimary(isPrimary: boolean) {
  return createSelector(selectAllCompleteFlightInstanceEntities, (entities) =>
    entities.filter((e) => e.isPrimary === isPrimary).valueSeq(),
  );
}
//#endregion Selectors
